import type { TFrameOptions, TRunPlanOptional } from './types';

import { VerificationBuilder } from './builders/verification';
import { RemoteViewFactory } from './factories/remote_view';
import { RenderNodeBuilder } from './builders/render_node';
import { ScaffoldFactory } from './factories/scaffold';
import { LifeCycleBridge } from './bridges/life_cycle';
import { RunPlanBuilder } from './builders/run_plan';

import { logInfo, logError } from './services/log';
import { merge } from './services/merge';

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window {
    HIP: {
      version: string;
    };
  }
}

/**
 * manages a connection to a single remote view so it can be invoked in an application
 * safely and with the required dependencies
 */
export class Frame {
  readonly bridges = {
    lifeCycle: new LifeCycleBridge()
  };

  /**
   * hold all builders to write frame
   */
  readonly creators = {
    verification: new VerificationBuilder(),
    renderNode: new RenderNodeBuilder(),
    remoteView: new RemoteViewFactory(),
    scaffold: new ScaffoldFactory(),
    runPlan: new RunPlanBuilder()
  };

  public options: TFrameOptions;

  readonly defaultSettings: TRunPlanOptional = {
    remote_view: {
      title: 'No Title',
      index_file: 'index'
    },
    scaffold: {
      type: 'iframe',
      region_tag: 'section',
      attributes: {
        style: 'display: block; border: 0; min-height: 100vh; width: 100%;',
        referrer: 'no-referrer',
        loading: 'lazy'
      },
      meta: {
        included_tags: [],
        exclude_default_tags: false
      }
    },
    hif: {
      is_active: true,
      auto_start: false,
      systems: [],
      version: window?.HIP?.version,
      logging: ['failure'],
      release: 'prod',
      included_crates: {},
      loaded_crates: {},
      attributes: [],
      settings: {
        constants: {},
        artifacts: [],
        flags: [['/pip/pi-ip/insert-css-after-script', true]]
      }
    }
  };

  /**
   * @param settings
   */
  public constructor(settings?: Partial<TFrameOptions>) {
    this.configure(settings);
  }

  /**
   * configure the frame
   *
   * @param settings
   */
  configure(settings?: Partial<TFrameOptions>) {
    this.options = Object.assign(
      {
        host: 'https://cdn.eab.com/run_plan',
        debug: false,
        settings: {}
      },
      settings ?? {}
    ) as TFrameOptions;

    return this;
  }

  /**
   * @deprecated use startup
   */
  async invoke() {
    return this.startup();
  }

  /**
   * Invoke run plan and load the remote view into the target application
   */
  async startup() {
    try {
      await this.creators.verification.verifySettings(this.options);
      logInfo(this.options, `pre-verification complete`);

      const runPlan = await this.creators.runPlan.create(this.options);
      logInfo(runPlan, `pull run-plan`);

      runPlan.frame = this.options;
      logInfo(runPlan.frame, `assign frame settings to run-plan`);

      if (runPlan.initialize) {
        await runPlan.initialize();
        logInfo(runPlan, `run initialize`);
      }

      ['hif', 'scaffold', 'remote_view'].forEach((settingsKey: 'hif' | 'scaffold' | 'remote_view') => {
        runPlan[settingsKey] = merge(this.defaultSettings[settingsKey], runPlan[settingsKey]);
      });
      logInfo(runPlan, `merged defaults to run-plan`);

      if (runPlan.activate) {
        await runPlan.activate();
        logInfo(runPlan, `run activate`);
      }

      const scaffold = await this.creators.scaffold.create(runPlan);
      logInfo(scaffold, `create scaffold`);

      const remoteView = await this.creators.remoteView.create(runPlan);
      logInfo(remoteView, `create remote view facade`);

      const codeHead = await remoteView.writeHead();
      logInfo(codeHead, `write index head code`);

      const codeBody = await remoteView.writeBody();
      logInfo(codeBody, `write index body code`);

      const codeIndex = await remoteView.writeIndex(runPlan, codeHead, codeBody);
      logInfo(codeIndex, `write index html code`);

      let code = await scaffold.weaveCode(codeHead, codeBody, codeIndex);
      logInfo(code, `weave head, body, and index into final code block`);

      if (runPlan.interceptCode) {
        code = (await runPlan.interceptCode(code)) ?? code;
        logInfo(code, `run interceptCode`);
      }

      let scaffoldEl = await scaffold.create(runPlan);
      logInfo(scaffoldEl, `crate scaffold element`);

      if (runPlan.interceptScaffold) {
        scaffoldEl = (await runPlan.interceptScaffold(scaffoldEl)) ?? scaffoldEl;
        logInfo(code, `run interceptScaffold`);
      }

      await scaffold.injectCode(scaffoldEl, code);
      logInfo(code, `inject code to scaffold`);

      let renderTo = await this.creators.renderNode.create(this.options);
      if (!renderTo) {
        throw `render-to ${this.options.render_to} was not found`;
      }

      if (runPlan.interceptRenderTo) {
        renderTo = (await runPlan.interceptRenderTo(renderTo)) ?? renderTo;
        logInfo(code, `run interceptRenderTo`);
      }

      await scaffold.beforeMount(scaffoldEl, renderTo, runPlan);
      logInfo({ scaffoldEl, renderTo, runPlan }, `run before mount`);

      renderTo.append(scaffoldEl);
      logInfo(renderTo, `add scaffold to render-to`);

      const scriptContext = await scaffold.computeScriptContext(scaffoldEl);
      logInfo(scriptContext, `compute startup script context`);

      await remoteView.startupRemoteView(scriptContext);
      logInfo(remoteView, `startup remote view`);

      await this.bridges.lifeCycle.startup(runPlan, remoteView, scaffold);
      logInfo(remoteView, `startup bridge between remote view and scaffold`);

      await scaffold.afterMount(scaffoldEl, renderTo, runPlan);
      logInfo({ scaffoldEl, renderTo, runPlan }, `run after mount`);

      await this.creators.verification.verifyRunPlan(runPlan);
      logInfo(runPlan, `verification complete`);
    } catch (err) {
      logError(err);
      throw new Error(err);
    }

    return this;
  }

  /**
   * shutdown the frame by running through the run-plan shutdown steps
   */
  async shutdown() {
    try {
      await this.creators.verification.verifySettings(this.options);
      logInfo(this.options, `pre-verification complete`);

      const runPlan = await this.creators.runPlan.create(this.options);
      logInfo(runPlan, `pull run-plan`);

      runPlan.frame = this.options;
      logInfo(runPlan.frame, `assign frame settings to run-plan`);

      ['hif', 'scaffold', 'remote_view'].forEach((settingsKey: 'hif' | 'scaffold' | 'remote_view') => {
        runPlan[settingsKey] = merge(this.defaultSettings[settingsKey], runPlan[settingsKey]);
      });
      logInfo(runPlan, `merged defaults to run-plan`);

      const scaffold = await this.creators.scaffold.create(runPlan);
      logInfo(scaffold, `create scaffold`);

      const remoteView = await this.creators.remoteView.create(runPlan);
      logInfo(remoteView, `create remote view facade`);

      if (runPlan.unmount) {
        await runPlan.unmount(scaffold);
        logInfo(runPlan, `run unmount`);
      }

      const renderTo = await this.creators.renderNode.locate(this.options);
      if (renderTo) {
        await scaffold.destroy(runPlan, renderTo);
        logInfo(runPlan, `destroy scaffold element`);
      }

      await this.bridges.lifeCycle.shutdown(runPlan, remoteView, scaffold);
      logInfo(remoteView, `shutdown bridge between remote view and scaffold`);

      if (runPlan.deactivate) {
        await runPlan.deactivate();
        logInfo(runPlan, `run deactivate`);
      }

      if (runPlan.uninitialize) {
        await runPlan.uninitialize();
        logInfo(runPlan, `run uninitialize`);
      }
    } catch (e) {
      logError(e);
    }

    return this;
  }
}
